import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef
} from 'react'
import { Box, Typography } from '@mui/material'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { createWorkOrder } from '../config/api'
import { GenericDLL } from '../../../components/Almacen/UI/GenericDLL'
import { useWarehouses } from '../../../hooks'
import { Order } from '../../../interfaces/Order/Order'

interface CreateWorkOrderProps {
  selectedOrderIds: number[]
  orders: Order[]
  onCanSubmitChange: (canSubmit: boolean) => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

interface WorkOrderType {
  idTipoOT: number
  tipoOT: string
}

export const EnumWorkOrderType: WorkOrderType[] = [
  {
    idTipoOT: 1,
    tipoOT: 'INBOUND'
  },

  {
    idTipoOT: 2,
    tipoOT: 'OUTBOUND'
  }
]

export interface CreateWorkOrderRef {
  submit: () => Promise<void>
  canSubmit: () => boolean
  isLoading: () => boolean
}

export const CreateWorkOrder = forwardRef<
  CreateWorkOrderRef,
  CreateWorkOrderProps
>(
  (
    { selectedOrderIds, orders, onCanSubmitChange, onSuccess, onError },
    ref
  ) => {
    const { state, callApi } = useApi()
    const [selectedWarehouse, setSelectedWarehouse] = useState<number>(0)

    const {
      warehousesState: { warehouses },
      getWarehouses
    } = useWarehouses()

    const isLoading = state.status === Status.Loading

    useEffect(() => {
      if (warehouses?.length === 0) {
        getWarehouses()
      } else if (warehouses?.length === 1) {
        // Si solo hay un almacén, seleccionarlo automáticamente
        setSelectedWarehouse(warehouses[0].id)
      }
    }, [warehouses])

    // Actualizar el estado de canSubmit en el componente padre
    useEffect(() => {
      const isLoading = state.status === Status.Loading
      const canSubmit = selectedWarehouse !== 0 && !isLoading
      onCanSubmitChange(canSubmit)
    }, [selectedWarehouse, state.status, onCanSubmitChange])

    const handleSelectedWarehouse = (
      event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      setSelectedWarehouse(parseInt(event.target.value))
    }

    const getOrderReferenceId = (orderId: number): string => {
      const order = orders.find((o) => o.id === orderId)
      return order ? order.referenceId.toString() : orderId.toString()
    }

    const handleSubmit = async () => {
      if (selectedWarehouse === 0) {
        return
      }

      const workOrderData = {
        typeWorkOrderId: EnumWorkOrderType[1].idTipoOT,
        workOrderDetails: selectedOrderIds.map((id) => ({ orderId: id })),
        warehouseId: selectedWarehouse
      }

      try {
        const response = await callApi(createWorkOrder(workOrderData))
        const otId = response.id
        const message =
          selectedOrderIds.length > 1
            ? `Se han enviado a procesar las OT seleccionadas.`
            : `Se ha creado correctamente la Orden de Trabajo N° ${otId}`
        onSuccess(message)
      } catch (error) {
        const errorMessage =
          (error as any) || 'Error al crear la orden de trabajo'
        onError(errorMessage)
      }
    }

    // Exponer funciones al componente padre mediante ref
    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      canSubmit: () => selectedWarehouse !== 0,
      isLoading: () => state.status === Status.Loading
    }))

    return (
      <Box p={3}>
        <Box mb={3}>
          <Typography variant='body1' color='textSecondary'>
            Cantidad de pedidos seleccionados:{' '}
            <strong>{selectedOrderIds.length}</strong>
          </Typography>
          <Typography variant='body1' color='textSecondary'>
            IDs de Pedidos seleccionados:{' '}
            <strong>
              {selectedOrderIds
                .slice(0, 5)
                .map((id) => getOrderReferenceId(id))
                .join(', ')}
              {selectedOrderIds.length > 5 && '...'}
            </strong>
          </Typography>
        </Box>

        <Box mt={3} mb={2} display='flex' justifyContent='center'>
          <GenericDLL
            initialValues={warehouses || []}
            onChange={handleSelectedWarehouse}
            title='Bodega'
            name='warehouseName'
            valueKey='id'
            value={selectedWarehouse}
            variant='outlined'
            disabled={warehouses?.length === 1 || isLoading}
            style={{ width: '60%' }}
          />
        </Box>
      </Box>
    )
  }
)
